"use client";

import React, { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/utils/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { showError, showSuccess } from "@/utils/toast";

interface SignaturePadProps {
  documentData: any;
  documentType: "invoice" | "quote";
  tableName: "invoices" | "quotes";
  onSignComplete: () => void;
}

export const SignaturePad: React.FC<SignaturePadProps> = ({ documentData, documentType, tableName, onSignComplete }) => {
  const { profile } = useAuth();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Resize canvas for crisp drawing on HiDPI
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const ratio = window.devicePixelRatio || 1;
    const width = 800;
    const height = 240;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(ratio, ratio);
    // Init background
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
  }, []);

  const getCtx = () => canvasRef.current?.getContext("2d") || null;

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = getCtx();
    if (!ctx) return;
    setIsDrawing(true);
    const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const ctx = getCtx();
    if (!ctx) return;
    const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const handlePointerUp = () => {
    const ctx = getCtx();
    if (!ctx) return;
    setIsDrawing(false);
    ctx.closePath();
  };

  const clearCanvas = () => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // white background
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = async () => {
    if (!profile) {
      showError("You must be logged in to sign.");
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;

    setIsSaving(true);
    try {
      // Convert to blob
      const blob: Blob | null = await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
      if (!blob) throw new Error("Failed to capture signature");

      const bucket = "signatures";
      const path = `${documentType}s/${documentData.id}/${Date.now()}-signature.png`;

      const { error: uploadError } = await supabase.storage.from(bucket).upload(path, blob, { contentType: "image/png", upsert: false });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
      const signatureUrl = urlData?.publicUrl;
      if (!signatureUrl) throw new Error("Failed to get signature URL");

      const { error: updateError } = await supabase
        .from(tableName)
        .update({
          signature_url: signatureUrl,
          signed_at: new Date().toISOString(),
          signer_id: profile.id,
          consent_given: true,
        })
        .eq("id", documentData.id);

      if (updateError) throw updateError;

      showSuccess("Signature saved!");
      onSignComplete();
    } catch (err: any) {
      console.error("Signature save error:", err);
      showError(err.message || "Failed to save signature");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        Please sign inside the box below. Your signature will be saved to this {documentType}.
      </div>
      <div className="border rounded-md p-3 bg-white">
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          className="w-full h-60 touch-none"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={clearCanvas}>Clear</Button>
        <Button onClick={saveSignature} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Signature"}
        </Button>
      </div>
    </div>
  );
};